<template>
  <ModuleWithHeading
    v-if="results?.length > 5"
    id="recommended-heading"
    class="m-recommended-artwork"
    :heading-text="heading"
  >
    <div class="m-recommended-artwork__container">
      <ListingCarousel
        size="xsmall"
        :items="results"
        :list-aria-id="'recommended-heading'"
      >
        <!-- eslint-disable vue/no-useless-template-attributes -->
        <template v-if="results" ref="carouselSlot" #default="{ item, index }">
          <ImageAndCaption
            v-bind="item"
            :key="index"
            :index="index"
            :heading-level="3"
          >
            <template v-if="showSimilarResults == true" #afterMetadata>
              <span class="m-recommended-artwork__reasons">
                {{ item.search_type.replaceAll("_", " ") }}
              </span>
            </template>
          </ImageAndCaption>
        </template>
      </ListingCarousel>
    </div>
    <SimilarSearchResults
      v-if="showSimilarResults == true"
      :indexed-id="indexedId"
      :human-made-object="humanMadeObject"
      :similar-search-results="rawResults"
    />
  </ModuleWithHeading>
</template>

<script>
import {
  useLocalization,
  ListingCarousel,
  alignImageBaseMixin,
  ImageAndCaption,
  useEventBus
} from "@thegetty-private/getty-ui";

import SimilarSearchResults from "./similarSearchResults/index.vue";
import ModuleWithHeading from "@/components/atoms/moduleWithHeading/index.vue";
import { getRecommendedArtwork } from "@/stores/localActionSupport";
import { getApiPath, showSimilarResults } from "@/helpers/configHelper";

import { transformSearchResultToThumbnailItemWithArtist } from "@/helpers/SearchItemHelper";

export default {
  name: "RecommendedArtwork",
  components: {
    ImageAndCaption,
    ListingCarousel,
    ModuleWithHeading,
    SimilarSearchResults
  },
  mixins: [alignImageBaseMixin],
  provide() {
    return {
      analyticsListener: (event, url, to) => {
        this.logEvent(to);
      }
    };
  },
  props: {
    /**
     * The indexedId
     */
    indexedId: {
      type: String,
      required: true
    },
    /**
     * the HumanMadeObject from the store
     */
    humanMadeObject: {
      type: Object,
      required: false,
      default: undefined
    },
    /**
     * a way to inject a static object into the page
     */
    storybookData: {
      type: Object,
      default: null
    }
  },
  setup(props) {
    return {
      ...useLocalization({
        baseLocaleKey: props?.baseLocaleKey
      })
    };
  },
  data() {
    return {
      /**
       * The Aria label for the search results
       */
      ariaHeading: this.translatePart("recommended_artwork.aria_heading"),
      /**
       * The heading for the section
       */
      heading: this.translatePart("recommended_artwork.heading"),
      /**
       * Whether we have values or not
       */
      isVisible: false,
      /**
       * Letting the AlignImageBase know it should use css-grid
       */
      useGrid: true,
      /**
       * the results of the search
       */
      results: [],
      /**
       * the untransformed results
       */
      rawResults: {},
      /**
       * Event bus composable instance
       */
      eventBus: useEventBus()
    };
  },
  computed: {
    /**
     * The search parameters for the related search results
     */
    searchParams() {
      return {
        id: this.indexedId
      };
    },
    /**
     * Checks feature flag for SimilarSearchResults
     */
    showSimilarResults() {
      return showSimilarResults();
    }
  },
  mounted() {
    this.fetchData();
  },
  async beforeUnmount() {
    await this.$nextTick();
  },
  methods: {
    /**
     * Log the click event
     *
     * @param {object} to
     */
    logEvent(to) {
      let item =
        this.results.filter((item) => to.state.indexedId == item.id)[0] || [];

      this.eventBus.emit("AnalyticsEvent", {
        category: "recommended__" + item.search_type,
        action: "Recommended Artwork Click",
        label: this.indexedId,
        noninteraction: true
      });
    },
    /**
     * Makes the request for similar items
     * @async
     */
    async fetchData() {
      if (this.storybookData) {
        this.rawResults = this.storybookData.rawResults;
        this.results = this.storybookData.results;
      }

      let results = await getRecommendedArtwork(
        this.searchParams,
        getApiPath("/")
      );
      if (results?.similar) {
        this.results = results?.similar?.map((item) => {
          let result = transformSearchResultToThumbnailItemWithArtist(
            item,
            false,
            this.translatePart
          );

          result.search_type = item.search_type;
          return result;
        });
        this.rawResults = results;
      }
    }
  }
};
</script>
<style lang="scss">
@import "./index.scss";
</style>
